import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Activity, BellRing, Smartphone, TrendingDown, ArrowRight, Flag, Wifi, ShieldCheck, Award } from 'lucide-react';
import { heroData, features, productCategories, trustBadges } from '../data/mock';
import { testimonials } from '../data/testimonials';
import TestimonialCard from '../components/TestimonialCard';

const iconMap = {
  Activity, BellRing, Smartphone, TrendingDown, Flag, Wifi, ShieldCheck, Award
};

const Home = () => {
  const handleBookDemo = () => {
    window.location.href = '/contact';
  };

  const handleTalkToExpert = () => {
    const phone = "+919217401431";
    window.open(`https://wa.me/${phone}?text=Hi, I'd like to speak with an expert about Niloman HX Smart RO systems.`, '_blank');
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-white via-cyan-50 to-teal-50 pt-20 pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSIjMDBCNEQ4IiBzdHJva2Utd2lkdGg9IjAuNSIgb3BhY2l0eT0iMC4xIi8+PC9nPjwvc3ZnPg==')] opacity-40"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in">
              <Badge className="bg-gradient-to-r from-cyan-500 to-teal-500 text-white border-0 px-4 py-1">
                IoT-Powered Alkaline Water Purification
              </Badge>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                {heroData.title}
              </h1>
              
              <p className="text-xl text-gray-600 leading-relaxed">
                {heroData.subtitle}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={handleBookDemo}
                  size="lg" 
                  className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white shadow-lg hover:shadow-xl transition-all duration-300 text-lg px-8"
                >
                  {heroData.ctaPrimary}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  onClick={handleTalkToExpert}
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-cyan-500 text-cyan-600 hover:bg-cyan-50 text-lg px-8 transition-all duration-300"
                >
                  {heroData.ctaSecondary}
                </Button>
              </div>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-6 pt-4">
                {trustBadges.map((badge) => {
                  const Icon = iconMap[badge.icon];
                  return (
                    <div key={badge.id} className="flex items-center gap-2 text-gray-700">
                      <Icon className="h-5 w-5 text-teal-600" />
                      <span className="text-sm font-medium">{badge.title}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="relative lg:h-[600px] animate-slide-in-right">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 to-teal-500 rounded-3xl blur-3xl opacity-20"></div>
              <img 
                src={heroData.heroImage}
                alt="Niloman HX Smart RO System"
                className="relative rounded-3xl shadow-2xl w-full h-full object-cover border-4 border-white"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose Niloman HX?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the next generation of water purification with intelligent monitoring and control
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = iconMap[feature.icon];
              return (
                <Card 
                  key={feature.id} 
                  className="border-2 hover:border-cyan-400 transition-all duration-300 hover:shadow-lg group"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <CardHeader>
                    <div className="w-14 h-14 bg-gradient-to-br from-cyan-100 to-teal-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Icon className="h-7 w-7 text-cyan-600" />
                    </div>
                    <CardTitle className="text-xl">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Alkaline Water Section */}
      <section data-testid="alkaline-water-section" className="py-20 bg-gradient-to-br from-gray-50 to-cyan-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Image - Left */}
            <div className="relative group">
              <div className="absolute -inset-4 bg-gradient-to-br from-cyan-200/40 to-teal-200/40 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500"></div>
              <img
                src="https://customer-assets.emergentagent.com/job_4615497a-4b33-48b0-85aa-482cfbf6f519/artifacts/z03tvx9e_image.png"
                alt="Pure alkaline water splash"
                data-testid="alkaline-water-image"
                className="relative rounded-2xl shadow-2xl w-full object-cover border-4 border-white/80"
              />
            </div>

            {/* Content - Right */}
            <div className="space-y-8">
              <div>
                <Badge className="bg-gradient-to-r from-cyan-500 to-teal-500 text-white border-0 px-4 py-1 mb-4">
                  Health & Wellness
                </Badge>
                <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight mb-4">
                  Effects of Alkaline Water on Health and Mood
                </h2>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                  Alkaline water has a pH above 8 (typically pH 8–9), making it less acidic than regular drinking water (pH ~7). It often contains alkaline minerals like calcium, potassium, magnesium, and bicarbonate. It can be naturally alkaline (from springs or mineral wells) or artificially made through ionization or adding minerals.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-5 group/item" data-testid="alkaline-point-1">
                  <span className="shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center text-white font-bold text-lg shadow-md group-hover/item:scale-110 transition-transform duration-300">
                    01
                  </span>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Slightly Sweet Or Refreshing</h3>
                    <p className="text-gray-500 text-sm mt-1">A naturally smooth taste that sets it apart from regular water.</p>
                  </div>
                </div>

                <div className="flex items-start gap-5 group/item" data-testid="alkaline-point-2">
                  <span className="shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center text-white font-bold text-lg shadow-md group-hover/item:scale-110 transition-transform duration-300">
                    02
                  </span>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Alkaline Water Hydrates Better Than Regular Water</h3>
                    <p className="text-gray-500 text-sm mt-1">Enhanced mineral content supports superior cellular hydration.</p>
                  </div>
                </div>

                <div className="flex items-start gap-5 group/item" data-testid="alkaline-point-3">
                  <span className="shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center text-white font-bold text-lg shadow-md group-hover/item:scale-110 transition-transform duration-300">
                    03
                  </span>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Alkaline Water Can Prevent Or Slow Cancer, Boost Immunity, Or Reverse Aging</h3>
                    <p className="text-gray-500 text-sm mt-1">Antioxidant-rich alkaline water helps neutralize free radicals in the body.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Categories Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Smart RO Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From compact home units to commercial systems, we have the perfect solution for every need
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {productCategories.map((category, index) => (
              <Card 
                key={category.id}
                className="overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-all duration-300 group cursor-pointer"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <Badge className="absolute top-4 right-4 bg-white text-gray-900">
                    {category.category}
                  </Badge>
                </div>
                <CardHeader>
                  <CardTitle className="text-2xl">{category.name}</CardTitle>
                  <CardDescription className="text-base">
                    {category.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Link to="/products">
                    <Button variant="link" className="text-cyan-600 p-0 h-auto font-semibold group-hover:translate-x-2 transition-transform">
                      Learn More <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/products">
              <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white">
                View All Products
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-600 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Experience Smart Water Purification?
          </h2>
          <p className="text-xl mb-8 text-cyan-50">
            Book a free demo today and see how Niloman HX can transform your water quality monitoring
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={handleBookDemo}
              size="lg" 
              className="bg-white text-cyan-600 hover:bg-gray-100 text-lg px-8"
            >
              Book Free Demo
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              onClick={handleTalkToExpert}
              size="lg" 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white/10 text-lg px-8"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>


    </div>
  );
};

export default Home;
